import { useState, useMemo } from "react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import {
  Users,
  AlertCircle,
  Plus,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  ExternalLink,
  Clock,
  CalendarCheck,
  UserCheck,
  UserX,
} from "lucide-react";

import { CompactKpiCard } from "@/components/kpi-card";

import { toast } from "sonner";

import { DataTableEnhanced, type ColumnSort, type ColumnDef } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';

import {
  type ResourceRequest,
  type SchedulingSummary,
  type SchedulingBooking,
  type RoleCoverage,
  type SchedulingWarning,
  type BookingStatus,
  type RoleCoverageStatus,
} from "@/lib/mock-scheduling-data";


import { formatDate, cn } from "@/lib/utils";


function getBookingStatusStyle(status: BookingStatus): string {
  switch (status) {
    case 'Confirmed':  return 'bg-success-100 text-success-800 border-success-200';
    case 'Planned':    return 'bg-blue-100 text-blue-800 border-blue-200 border-dashed';
    case 'Cancelled':  return 'bg-error-100 text-error-800 border-error-200 line-through';
    case 'Completed':  return 'bg-neutral-100 text-neutral-600 border-neutral-200';
    default:           return 'bg-neutral-100 text-neutral-600 border-neutral-200';
  }
}

/** Returns Tailwind classes + label for a RoleCoverageStatus cell */
function getRoleCoverageStyle(status: RoleCoverageStatus): {
  dot: string;
  label: string;
  text: string;
} {
  switch (status) {
    case 'FullyCovered':
      return { dot: 'bg-success-500', label: 'Fully Covered',      text: 'text-success-700' };
    case 'PartiallyCovered':
      return { dot: 'bg-warning-500', label: 'Partially Covered', text: 'text-warning-700' };
    case 'NotCovered':
      return { dot: 'bg-error-500',    label: 'Not Covered',       text: 'text-error-700'   };
    case 'OverScheduled':
      return { dot: 'bg-orange-500', label: 'Over-scheduled',    text: 'text-orange-700'};
    default:
      return { dot: 'bg-neutral-400',   label: 'Unknown',           text: 'text-neutral-600'  };
  }
}


// ---------------------------------------------------------------------------
// ResourcingTabContent — component
// ---------------------------------------------------------------------------

interface ResourcingTabContentProps {
  bookings: SchedulingBooking[];
  roleCoverage: RoleCoverage[];
  warnings: SchedulingWarning[];
  summary: SchedulingSummary;
  schedulerName: string;
  /** Base URL for the connected scheduler (deep-linking) */
  schedulerUrl?: string;
  onRaiseRequest?: (roleName?: string) => void;
  resourceRequests?: ResourceRequest[];
  /** Whether a Resource Budget exists for this project */
  hasResourceBudget?: boolean;
  /** Navigate the user to the Resource Budget tab to create one */
  onNavigateToResourceBudget?: () => void;
  /** Project ID used for deep-linking to the scheduler */
  projectId?: string;
}

function ResourcingTabContent({
  bookings,
  roleCoverage,
  summary,
  schedulerName,
  schedulerUrl,
  onRaiseRequest,
  resourceRequests = [],
  hasResourceBudget = false,
  onNavigateToResourceBudget,
  projectId,
}: ResourcingTabContentProps) {
  // Expandable role rows in the Role Coverage table
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());


  // "Open in Scheduler" confirmation dialog
  const [openSchedulerDialogOpen, setOpenSchedulerDialogOpen] = useState(false);
  // Which sub-tab is active: bookings | coverage | requests
  const [resourcingSubTab, setResourcingSubTab] = useState<'bookings' | 'coverage' | 'requests'>('requests');

  // Bookings table sort/pagination
  const [bookingColumnSorts, setBookingColumnSorts] = useState<ColumnSort[]>([{ columnId: 'startDate', direction: 'asc' }]);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingPageSize, setBookingPageSize] = useState(25);

  // Role Coverage table sort/pagination
  const [coverageColumnSorts, setCoverageColumnSorts] = useState<ColumnSort[]>([]);
  const [coveragePage, setCoveragePage] = useState(1);
  const [coveragePageSize, setCoveragePageSize] = useState(25);

  // Resource Requests table sort/pagination
  const [requestsColumnSorts, setRequestsColumnSorts] = useState<ColumnSort[]>([{ columnId: 'raisedAt', direction: 'desc' }]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPageSize, setRequestsPageSize] = useState(25);

  const toggleRoleExpand = (roleId: string) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  // Derived KPIs from summary
  const totalUnmetHours = summary.unmetRoles.reduce((sum, r) => sum + r.gapHours, 0);

  // Bookings sorted + paginated
  const sortedBookings = useMemo(() => {
    const numCols = new Set(['totalHours', 'actualHours']);
    return [...bookings].sort((a, b) => {
      for (const { columnId, direction } of bookingColumnSorts) {
        const aVal = a[columnId as keyof SchedulingBooking];
        const bVal = b[columnId as keyof SchedulingBooking];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [bookings, bookingColumnSorts]);
  const bookingPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedBookings.length, bookingPage, bookingPageSize),
    [sortedBookings.length, bookingPage, bookingPageSize],
  );
  const paginatedBookings = useMemo(
    () => paginateArray(sortedBookings, bookingPage, bookingPageSize).data,
    [sortedBookings, bookingPage, bookingPageSize],
  );

  // Role Coverage sorted + paginated
  const sortedCoverage = useMemo(() => {
    const numCols = new Set(['budgetHours', 'scheduledHours', 'actualHours', 'remainingHours', 'coveragePercent']);
    return [...roleCoverage].sort((a, b) => {
      for (const { columnId, direction } of coverageColumnSorts) {
        const aVal = a[columnId as keyof RoleCoverage];
        const bVal = b[columnId as keyof RoleCoverage];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [roleCoverage, coverageColumnSorts]);
  const coveragePagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedCoverage.length, coveragePage, coveragePageSize),
    [sortedCoverage.length, coveragePage, coveragePageSize],
  );
  const paginatedCoverage = useMemo(
    () => paginateArray(sortedCoverage, coveragePage, coveragePageSize).data,
    [sortedCoverage, coveragePage, coveragePageSize],
  );

  // Resource Requests sorted + paginated
  const sortedRequests = useMemo(() => {
    const numCols = new Set(['hoursRequired']);
    return [...resourceRequests].sort((a, b) => {
      for (const { columnId, direction } of requestsColumnSorts) {
        const aVal = a[columnId as keyof ResourceRequest];
        const bVal = b[columnId as keyof ResourceRequest];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [resourceRequests, requestsColumnSorts]);
  const requestsPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedRequests.length, requestsPage, requestsPageSize),
    [sortedRequests.length, requestsPage, requestsPageSize],
  );
  const paginatedRequests = useMemo(
    () => paginateArray(sortedRequests, requestsPage, requestsPageSize).data,
    [sortedRequests, requestsPage, requestsPageSize],
  );

  return (
    <div className="space-y-4">

      {/* ── Header row: title + Open in Scheduler button ──────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            Resourcing
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Booking coverage and scheduling data from {schedulerName}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setOpenSchedulerDialogOpen(true)}
        >
          <ExternalLink className="h-4 w-4" />
          Open in {schedulerName}
        </Button>
      </div>

      {/* ── KPI Summary Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <CompactKpiCard
          title="Scheduled Hours"
          value={`${summary.totalScheduledHours} / ${summary.totalBudgetHours} hrs`}
          icon={Clock}
          description={<Progress value={summary.coveragePercent} className="mt-1 h-1.5" />}
          accentVariant={
            summary.totalBudgetHours === 0 ? 'info'
            : summary.totalScheduledHours >= summary.totalBudgetHours ? 'success'
            : summary.totalScheduledHours > 0 ? 'warning'
            : 'error'
          }
          className="shadow-none"
        />
        <CompactKpiCard
          title="Booking Coverage"
          value={`${summary.coveragePercent}%`}
          icon={CalendarCheck}
          description={`${summary.confirmedBookings} confirmed · ${summary.plannedBookings} planned`}
          accentVariant={
            summary.coveragePercent >= 80 ? 'success'
            : summary.coveragePercent >= 50 ? 'warning'
            : 'error'
          }
          className="shadow-none"
        />
        <CompactKpiCard
          title="Confirmed Bookings"
          value={summary.confirmedBookings}
          icon={UserCheck}
          description={`${summary.totalBookings} total bookings`}
          accentVariant={
            summary.totalBookings === 0 ? 'info'
            : summary.confirmedBookings === summary.totalBookings ? 'success'
            : summary.confirmedBookings > 0 ? 'warning'
            : 'error'
          }
          className="shadow-none"
        />
        <CompactKpiCard
          title="Unfilled Hours"
          value={`${totalUnmetHours} hrs`}
          icon={UserX}
          description={`across ${summary.unmetRoles.length} role${summary.unmetRoles.length !== 1 ? 's' : ''}`}
          accentVariant={totalUnmetHours > 0 ? 'error' : 'success'}
          className="shadow-none"
        />
      </div>

      {/* ── Sub-tab switcher ───────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b">
        {(['requests', 'bookings', 'coverage'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setResourcingSubTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize flex items-center gap-1.5',
              resourcingSubTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground',
            )}
          >
            {tab === 'bookings' ? 'Bookings' : tab === 'coverage' ? 'Role Coverage' : (
              <>
                Resource Requests
                {resourceRequests.length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium h-4 px-1.5">
                    {resourceRequests.length}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {/* ── Bookings sub-tab ───────────────────────────────────────────────── */}
      {resourcingSubTab === 'bookings' && (
        <Card>
          <CardContent className="p-0">
            {bookings.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-6">
                <CalendarDays className="h-12 w-12 text-muted-foreground/40" />
                <div>
                  <p className="font-medium text-foreground">No bookings found for this project</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {hasResourceBudget
                      ? `Bookings will appear here once the resourcing team fulfils your resource requests in ${schedulerName}.`
                      : 'Create a Resource Budget to start raising resource requests and scheduling bookings.'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!hasResourceBudget && (
                    <Button variant="default" size="sm" onClick={() => onNavigateToResourceBudget?.()}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Create Resource Budget
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setOpenSchedulerDialogOpen(true)}>
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    Open in {schedulerName}
                  </Button>
                </div>
              </div>
            ) : (() => {
                const bookingColDefs: ColumnDef<SchedulingBooking, unknown>[] = [
                  {
                    id: 'personName',
                    header: 'Resource',
                    accessorKey: 'personName',
                    cell: ({ row }: { row: { original: SchedulingBooking } }) => (
                      <div className="flex items-center gap-2">
                        {row.original.personId ? (
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={row.original.personAvatar ?? undefined} alt={row.original.personName ?? ''} />
                            <AvatarFallback className="text-xs">
                              {(row.original.personName ?? '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-7 w-7 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                            <Users className="h-3.5 w-3.5 text-muted-foreground/50" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{row.original.personName ?? 'Unassigned'}</span>
                      </div>
                    ),
                  },
                  { id: 'roleName', header: 'Role', accessorKey: 'roleName', cell: ({ row }: { row: { original: SchedulingBooking } }) => <span className="text-sm text-muted-foreground">{row.original.roleName}</span> },
                  { id: 'startDate', header: 'Start Date', accessorKey: 'startDate', cell: ({ row }: { row: { original: SchedulingBooking } }) => <span className="text-sm">{formatDate(row.original.startDate)}</span> },
                  { id: 'endDate', header: 'End Date', accessorKey: 'endDate', cell: ({ row }: { row: { original: SchedulingBooking } }) => <span className="text-sm">{formatDate(row.original.endDate)}</span> },
                  { id: 'totalHours', header: 'Booked Hrs', accessorKey: 'totalHours', cell: ({ row }: { row: { original: SchedulingBooking } }) => <span className="text-sm font-medium">{row.original.totalHours}</span> },
                  {
                    id: 'actualHours',
                    header: 'Actual Hrs',
                    accessorKey: 'actualHours',
                    cell: ({ row }: { row: { original: SchedulingBooking } }) => row.original.actualHours !== null ? (
                      <span className={cn('font-medium text-sm',
                        (row.original.actualHours ?? 0) < row.original.totalHours ? 'text-warning-600'
                          : (row.original.actualHours ?? 0) > row.original.totalHours ? 'text-error-600'
                          : 'text-success-600',
                      )}>{row.original.actualHours}</span>
                    ) : <span className="text-muted-foreground">—</span>,
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    accessorKey: 'status',
                    enableSorting: false,
                    cell: ({ row }: { row: { original: SchedulingBooking } }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', getBookingStatusStyle(row.original.status))}>
                        {row.original.status}
                      </span>
                    ),
                  },
                  {
                    id: 'source',
                    header: 'Source',
                    accessorKey: 'source',
                    enableSorting: false,
                    cell: ({ row }: { row: { original: SchedulingBooking } }) => <span className="text-xs text-muted-foreground">{row.original.source === 'Projects' ? 'Raised in Projects' : 'Created in Scheduler'}</span>,
                  },
                  {
                    id: 'actions',
                    header: '',
                    enableSorting: false,
                    cell: ({ row }: { row: { original: SchedulingBooking } }) => (row.original.status === 'Confirmed' || row.original.status === 'Planned') ? (
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpenSchedulerDialogOpen(true)}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left"><p>View in {schedulerName}</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : null,
                  },
                ];
                return (
                  <DataTableEnhanced
                    columns={bookingColDefs}
                    data={paginatedBookings}
                    columnVisibility={Object.fromEntries(bookingColDefs.map(c => [c.id!, true]))}
                    columnSorts={bookingColumnSorts}
                    onColumnSortChange={(s) => { setBookingColumnSorts(s); setBookingPage(1); }}
                    emptyMessage={<span className="text-muted-foreground">No bookings found</span>}
                    paginationContent={
                      <PaginationControls
                        pagination={bookingPagination}
                        onPageChange={setBookingPage}
                        onPageSizeChange={(s) => { setBookingPageSize(s); setBookingPage(1); }}
                      />
                    }
                  />
                );
              })()
            }
          </CardContent>
        </Card>
      )}

      {/* ── Role Coverage sub-tab ──────────────────────────────────────────── */}
      {resourcingSubTab === 'coverage' && (
        <Card>
          <CardContent className="p-0">
            {(() => {
              const coverageColDefs: ColumnDef<RoleCoverage, unknown>[] = [
                {
                  id: 'roleName',
                  header: 'Role',
                  accessorKey: 'roleName',
                  cell: ({ row }: { row: { original: RoleCoverage } }) => {
                    const isExpanded = expandedRoles.has(row.original.roleId);
                    return (
                      <div className="flex items-center gap-2">
                        {row.original.people.length > 0
                          ? isExpanded
                            ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          : <span className="w-4 flex-shrink-0" />}
                        <span className="font-medium text-sm">{row.original.roleName}</span>
                      </div>
                    );
                  },
                },
                { id: 'budgetHours', header: 'Budget Hrs', accessorKey: 'budgetHours', cell: ({ row }: { row: { original: RoleCoverage } }) => <span className="text-sm">{row.original.budgetHours}</span> },
                { id: 'scheduledHours', header: 'Scheduled Hrs', accessorKey: 'scheduledHours', cell: ({ row }: { row: { original: RoleCoverage } }) => <span className="text-sm">{row.original.scheduledHours}</span> },
                { id: 'actualHours', header: 'Actual Hrs', accessorKey: 'actualHours', cell: ({ row }: { row: { original: RoleCoverage } }) => <span className="text-sm">{row.original.actualHours}</span> },
                { id: 'remainingHours', header: 'Remaining', accessorKey: 'remainingHours', cell: ({ row }: { row: { original: RoleCoverage } }) => <span className="text-sm">{row.original.remainingHours}</span> },
                {
                  id: 'coveragePercent',
                  header: 'Coverage',
                  accessorKey: 'coveragePercent',
                  cell: ({ row }: { row: { original: RoleCoverage } }) => (
                    <div className="flex items-center gap-2">
                      <Progress value={Math.min(row.original.coveragePercent, 100)} className="h-1.5 w-16" />
                      <span className="text-xs text-muted-foreground">{row.original.coveragePercent}%</span>
                    </div>
                  ),
                },
                {
                  id: 'status',
                  header: 'Status',
                  accessorKey: 'status',
                  enableSorting: false,
                  cell: ({ row }: { row: { original: RoleCoverage } }) => {
                    const style = getRoleCoverageStyle(row.original.status);
                    return (
                      <div className="flex items-center gap-1.5">
                        <span className={cn('h-2 w-2 rounded-full flex-shrink-0', style.dot)} />
                        <span className={cn('text-xs font-medium', style.text)}>{style.label}</span>
                      </div>
                    );
                  },
                },
                {
                  id: 'actions',
                  header: '',
                  enableSorting: false,
                  cell: ({ row }: { row: { original: RoleCoverage } }) => row.original.coveragePercent < 100 ? (
                    hasResourceBudget ? (
                      <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => onRaiseRequest?.(row.original.roleName)}>Raise Request</Button>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled>Raise Request</Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Create a Resource Budget first</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )
                  ) : null,
                },
              ];

              return (
                <DataTableEnhanced
                  columns={coverageColDefs}
                  data={paginatedCoverage}
                  columnVisibility={Object.fromEntries(coverageColDefs.map(c => [c.id!, true]))}
                  columnSorts={coverageColumnSorts}
                  onColumnSortChange={(s) => { setCoverageColumnSorts(s); setCoveragePage(1); }}
                  emptyMessage={<span className="text-muted-foreground">No role coverage data</span>}
                  onRowClick={(row) => {
                    const role = row as unknown as RoleCoverage;
                    if ((role as RoleCoverage).people?.length > 0) toggleRoleExpand((role as RoleCoverage).roleId);
                  }}
                  customRowRenderer={(row, defaultRow) => {
                    const role = row.original as unknown as RoleCoverage;
                    const isExpanded = expandedRoles.has(role.roleId);
                    return (
                      <>
                        {defaultRow}
                        {isExpanded && role.people.map(person => (
                          <tr key={person.personId} className="bg-muted/30">
                            <td className="px-4 py-2" colSpan={1}>
                              <div className="flex items-center gap-2 pl-10">
                                <span className="text-xs text-muted-foreground">└</span>
                                <span className="text-sm text-muted-foreground">{person.personName}</span>
                              </div>
                            </td>
                            <td />
                            <td className="px-4 py-2 text-right text-sm text-muted-foreground">{person.scheduledHours}</td>
                            <td className="px-4 py-2 text-right text-sm text-muted-foreground">{person.actualHours}</td>
                            <td colSpan={4} />
                          </tr>
                        ))}
                      </>
                    );
                  }}
                  paginationContent={
                    <PaginationControls
                      pagination={coveragePagination}
                      onPageChange={setCoveragePage}
                      onPageSizeChange={(s) => { setCoveragePageSize(s); setCoveragePage(1); }}
                    />
                  }
                />
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ── Resource Requests sub-tab ─────────────────────────────────────── */}
      {resourcingSubTab === 'requests' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Resource Requests</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Requests raised for the resourcing team to fill via {schedulerName}
                </CardDescription>
              </div>
              {hasResourceBudget ? (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onRaiseRequest?.()}>
                  <Plus className="h-3.5 w-3.5" />
                  New Request
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button size="sm" variant="outline" className="gap-1.5" disabled>
                          <Plus className="h-3.5 w-3.5" />
                          New Request
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create a Resource Budget first to raise requests</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {resourceRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-6">
                <CalendarDays className="h-10 w-10 text-muted-foreground/40" />
                <div>
                  <p className="font-medium text-foreground">No resource requests yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Raise a request from the Resource Budget tab to ask the resourcing team to fill a role.
                  </p>
                </div>
              </div>
            ) : (() => {
                const priorityLabel: Record<string, string> = { 'nice-to-have': 'Nice to Have', 'important': 'Important', 'critical': 'Critical' };
                const priorityStyle: Record<string, string> = { 'nice-to-have': 'bg-neutral-100 text-neutral-700 border-neutral-200', 'important': 'bg-blue-100 text-blue-700 border-blue-200', 'critical': 'bg-error-100 text-error-700 border-error-200' };
                const statusStyle: Record<string, string> = { 'Pending': 'bg-warning-100 text-warning-800 border-warning-200', 'InProgress': 'bg-blue-100 text-blue-800 border-blue-200', 'Filled': 'bg-success-100 text-success-800 border-success-200', 'Cancelled': 'bg-neutral-100 text-neutral-600 border-neutral-200' };
                const statusLabel: Record<string, string> = { 'Pending': 'Pending', 'InProgress': 'In Progress', 'Filled': 'Filled', 'Cancelled': 'Cancelled' };

                const requestColDefs: ColumnDef<ResourceRequest, unknown>[] = [
                  { id: 'roleName', header: 'Role', accessorKey: 'roleName', cell: ({ row }: { row: { original: ResourceRequest } }) => <span className="font-medium text-sm">{row.original.roleName}</span> },
                  { id: 'hoursRequired', header: 'Hours', accessorKey: 'hoursRequired', cell: ({ row }: { row: { original: ResourceRequest } }) => <span className="text-sm">{row.original.hoursRequired}</span> },
                  { id: 'startDate', header: 'From', accessorKey: 'startDate', cell: ({ row }: { row: { original: ResourceRequest } }) => <span className="text-sm">{formatDate(row.original.startDate)}</span> },
                  { id: 'endDate', header: 'To', accessorKey: 'endDate', cell: ({ row }: { row: { original: ResourceRequest } }) => <span className="text-sm">{formatDate(row.original.endDate)}</span> },
                  {
                    id: 'priority',
                    header: 'Priority',
                    accessorKey: 'priority',
                    enableSorting: false,
                    cell: ({ row }: { row: { original: ResourceRequest } }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', priorityStyle[row.original.priority] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200')}>
                        {priorityLabel[row.original.priority] ?? row.original.priority}
                      </span>
                    ),
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    accessorKey: 'status',
                    enableSorting: false,
                    cell: ({ row }: { row: { original: ResourceRequest } }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', statusStyle[row.original.status] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200')}>
                        {statusLabel[row.original.status] ?? row.original.status}
                      </span>
                    ),
                  },
                  { id: 'assignedTo', header: 'Assigned To', accessorKey: 'assignedTo', cell: ({ row }: { row: { original: ResourceRequest } }) => <span className="text-sm text-muted-foreground">{row.original.assignedTo ?? '—'}</span> },
                  {
                    id: 'raisedAt',
                    header: 'Raised',
                    accessorKey: 'raisedAt',
                    cell: ({ row }: { row: { original: ResourceRequest } }) => {
                      const daysAgo = Math.floor((Date.now() - new Date(row.original.raisedAt).getTime()) / (1000 * 60 * 60 * 24));
                      return <span className="text-xs text-muted-foreground">{daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`}</span>;
                    },
                  },
                ];

                return (
                  <DataTableEnhanced
                    columns={requestColDefs}
                    data={paginatedRequests}
                    columnVisibility={Object.fromEntries(requestColDefs.map(c => [c.id!, true]))}
                    columnSorts={requestsColumnSorts}
                    onColumnSortChange={(s) => { setRequestsColumnSorts(s); setRequestsPage(1); }}
                    emptyMessage={<span className="text-muted-foreground">No resource requests yet</span>}
                    className="rounded-none"
                    paginationContent={
                      <PaginationControls
                        pagination={requestsPagination}
                        onPageChange={setRequestsPage}
                        onPageSizeChange={(s) => { setRequestsPageSize(s); setRequestsPage(1); }}
                      />
                    }
                  />
                );
              })()
            }
          </CardContent>
        </Card>
      )}

      {/* ── Open in Scheduler confirmation dialog ─────────────────────────── */}
      <AlertDialog open={openSchedulerDialogOpen} onOpenChange={setOpenSchedulerDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open in {schedulerName}</AlertDialogTitle>
            <AlertDialogDescription>
              This will open {schedulerName} and navigate to this project's bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!schedulerUrl && (
            <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Deep link configuration is managed by your administrator in{' '}
                <strong>Settings → Integrations</strong>.
              </span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (schedulerUrl) {
                  const deepLink = projectId
                    ? `${schedulerUrl.replace(/\/$/, '')}/projects/${projectId}`
                    : schedulerUrl;
                  window.open(deepLink, '_blank');
                } else {
                  toast.info('Opening scheduler…', {
                    description: `Deep link not yet configured. Contact your administrator in Settings → Integrations.`,
                  });
                }
              }}
            >
              Open {schedulerName}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



    </div>
  );
}

export default ResourcingTabContent;
